import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DashboardId } from '@blisspointmedia/bpm-types/dist/dashboarding';
import { LayoutChange } from '../dashboard-types';

/**
 * One-shot "the user asked, via chat, to change this dashboard's layout" note, carried in the
 * react-query cache from its writer (AgentDrawer, which sees the agent's `set_card_size`,
 * `remove_card` and `move_card` tool calls) to its consumer (DashboardBuilder, which owns the layout
 * buffer).
 *
 * A shared-cache signal rather than AgentDrawer writing the layout itself, for the same reason
 * `usePendingPromotion` is one: the chat path then runs the SAME buffer-and-reflow the card's own
 * controls run, so an agent layout change is a pending dashboard change with the header's
 * Save/Discard, and cannot become a write the controls would never have made.
 *
 * Keyed by DASHBOARD, not by chart handle — a layout change belongs to a layout, and one turn can
 * make several ("make the tables full width", "move the table to the top and remove the ROAS chart").
 * An ARRAY, in call order, for the same reason: each change applies to the layout the previous one
 * left. No sessionStorage sidecar: a transient intent, not durable state.
 */
const pendingLayoutChangesKey = (company: string, dashboardId: DashboardId) => [
  'pending-layout-changes',
  company,
  dashboardId
];

export interface PendingLayoutChanges {
  pendingLayoutChanges: LayoutChange[];
  setPendingLayoutChanges: (changes: LayoutChange[]) => void;
  clearPendingLayoutChanges: () => void;
}

export const usePendingLayoutChanges = (
  company: string,
  dashboardId: DashboardId | null
): PendingLayoutChanges => {
  const queryClient = useQueryClient();
  const key = dashboardId
    ? pendingLayoutChangesKey(company, dashboardId)
    : ['pending-layout-changes', company, null];

  const { data: pendingLayoutChanges = [] } = useQuery<LayoutChange[]>({
    queryKey: key,
    queryFn: () => queryClient.getQueryData<LayoutChange[]>(key) ?? [],
    enabled: Boolean(company && dashboardId),
    staleTime: Infinity,
    gcTime: Infinity
  });

  const setPendingLayoutChanges = useCallback(
    (changes: LayoutChange[]) => {
      if (dashboardId) {
        queryClient.setQueryData(pendingLayoutChangesKey(company, dashboardId), changes);
      }
    },
    [queryClient, company, dashboardId]
  );

  const clearPendingLayoutChanges = useCallback(() => {
    if (dashboardId) {
      queryClient.setQueryData(pendingLayoutChangesKey(company, dashboardId), []);
    }
  }, [queryClient, company, dashboardId]);

  return { pendingLayoutChanges, setPendingLayoutChanges, clearPendingLayoutChanges };
};
